<template>
  <div class="kv-ve__color-field">
    <div class="kv-ve__color-swatch-wrap">
      <input class="kv-ve__color-native" type="color" :value="rgbValue" @input="onColorInput" />
      <div class="kv-ve__color-swatch-bg"></div>
      <div class="kv-ve__color-swatch" :style="{ backgroundColor: rgbaValue }"></div>
      <div class="kv-ve__color-alpha-wrap">
        <input
          class="kv-ve__color-alpha-slider"
          type="range"
          :min="0"
          :max="100"
          :value="alphaPercent"
          @input="onAlphaChange"
        />
        <div class="kv-ve__color-alpha-label">{{ alphaPercent }}%</div>
      </div>
    </div>
    <!-- i18n-validator-ignore-next-line: hex color format pattern -->
    <input
      class="form-control kv-ve__color-hex"
      :value="normalizedValue"
      :placeholder="hexPlaceholder"
      @change="onHexChange"
      @keyup.enter="onHexChange"
    />
  </div>
</template>

<script>
export default {
  name: "VeColorPicker",
  model: { prop: "value", event: "input" },
  props: {
    value: { type: String, default: "#000000" },
  },
  computed: {
    hexPlaceholder() {
      return "#RRGGBBAA";
    },
    normalizedValue() {
      return this.normalizeHex(this.value) || "#000000";
    },
    rgbValue() {
      // Extract RGB from hex (with or without alpha)
      const hex = this.normalizedValue;
      if (!hex) return "#000000";
      // If 8-digit, strip alpha
      if (hex.length === 9) return hex.substring(0, 7);
      return hex;
    },
    alphaPercent() {
      const hex = this.normalizedValue;
      if (!hex) return 100;
      // If 8-digit hex (#RRGGBBAA), extract alpha
      if (hex.length === 9) {
        const alphaHex = hex.substring(7, 9);
        const alpha = parseInt(alphaHex, 16);
        return Math.round((alpha / 255) * 100);
      }
      return 100;
    },
    rgbaValue() {
      const hex = this.normalizedValue;
      if (!hex) return "#000000";
      // If 8-digit, convert to rgba()
      if (hex.length === 9) {
        const r = parseInt(hex.substring(1, 3), 16);
        const g = parseInt(hex.substring(3, 5), 16);
        const b = parseInt(hex.substring(5, 7), 16);
        const a = parseInt(hex.substring(7, 9), 16) / 255;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }
      // 6-digit hex, return as-is
      return hex;
    },
  },
  methods: {
    normalizeHex(raw) {
      if (raw === null || raw === undefined) return null;
      let v = String(raw).trim();
      if (!v) return null;
      if (!v.startsWith("#")) v = `#${v}`;
      // Handle 3-digit hex
      const short = v.match(/^#([0-9a-f]{3})$/i);
      if (short) {
        const s = short[1];
        v = `#${s[0]}${s[0]}${s[1]}${s[1]}${s[2]}${s[2]}`;
      }
      // Handle 4-digit hex (with alpha)
      const shortAlpha = v.match(/^#([0-9a-f]{4})$/i);
      if (shortAlpha) {
        const s = shortAlpha[1];
        v = `#${s[0]}${s[0]}${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
      }
      // Accept 6-digit (#RRGGBB) or 8-digit (#RRGGBBAA)
      if (!/^#[0-9a-f]{6}([0-9a-f]{2})?$/i.test(v)) return null;
      return v.toLowerCase();
    },
    hexWithAlpha(rgbHex, alphaPercent) {
      const alpha = Math.round((alphaPercent / 100) * 255);
      const alphaHex = alpha.toString(16).padStart(2, "0");
      return `${rgbHex}${alphaHex}`;
    },
    onColorInput(event) {
      const rgbHex = event.target.value; // Always 6-digit from color input
      const alpha = this.alphaPercent;
      const fullHex = alpha < 100 ? this.hexWithAlpha(rgbHex, alpha) : rgbHex;
      this.$emit("input", fullHex);
    },
    onHexChange(event) {
      const normalized = this.normalizeHex(event.target.value);
      if (normalized) {
        // If user entered 6-digit, preserve current alpha
        if (normalized.length === 7) {
          const alpha = this.alphaPercent;
          const fullHex = alpha < 100 ? this.hexWithAlpha(normalized, alpha) : normalized;
          this.$emit("input", fullHex);
        } else {
          this.$emit("input", normalized);
        }
      } else {
        // Reset to current value if invalid
        event.target.value = this.normalizedValue;
      }
    },
    onAlphaChange(event) {
      const alphaPercent = Number(event.target.value);
      const rgbHex = this.rgbValue;
      const fullHex = alphaPercent < 100 ? this.hexWithAlpha(rgbHex, alphaPercent) : rgbHex;
      this.$emit("input", fullHex);
    },
    onAlphaInputChange(event) {
      const alphaPercent = Math.min(100, Math.max(0, Number(event.target.value) || 100));
      const rgbHex = this.rgbValue;
      const fullHex = alphaPercent < 100 ? this.hexWithAlpha(rgbHex, alphaPercent) : rgbHex;
      this.$emit("input", fullHex);
    },
  },
};
</script>

<style scoped lang="less">
.kv-ve__color-field {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  margin-bottom:24px;
}
.kv-ve__color-swatch-wrap {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: visible;
  background: #fff;
  flex: 0 0 auto;
}
.kv-ve__color-swatch-bg {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(45deg, #e5e7eb 25%, transparent 25%),
    linear-gradient(-45deg, #e5e7eb 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e5e7eb 75%),
    linear-gradient(-45deg, transparent 75%, #e5e7eb 75%);
  background-size: 8px 8px;
  background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
}
.kv-ve__color-native {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
}
.kv-ve__color-swatch {
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 1;
  border-radius: 8px;
  overflow: hidden;
}
.kv-ve__color-alpha-wrap {
  position: absolute;
  bottom: -24px;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  z-index: 3;
}
.kv-ve__color-alpha-slider {
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
}
.kv-ve__color-alpha-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #344054;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}
.kv-ve__color-alpha-slider::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #344054;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}
.kv-ve__color-alpha-label {
  font-size: 10px;
  color: #667085;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
}
.kv-ve__color-field .form-control.kv-ve__color-hex {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  letter-spacing: 0.01em;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  flex: 1;
}
</style>